import React, { useMemo } from 'react'

/**
 * Adjusts color brightness
 */
function adjustColor(hex, percent) {
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null
  }

  const rgbToHex = (r, g, b) => {
    return (
      '#' +
      [r, g, b]
        .map((x) => {
          const hex = Math.round(x).toString(16)
          return hex.length === 1 ? '0' + hex : hex
        })
        .join('')
    )
  }

  const rgb = hexToRgb(hex)
  if (!rgb) return hex

  const adjust = (value) => {
    const adjusted = value + (percent / 100) * 255
    return Math.max(0, Math.min(255, adjusted))
  }

  return rgbToHex(adjust(rgb.r), adjust(rgb.g), adjust(rgb.b))
}

/**
 * Generates shades/tints for a given color
 */
function generateShades(baseColor, count = 7) {
  if (!baseColor) return []

  const shades = []
  const step = 60 / (count - 1)

  for (let i = 0; i < count; i++) {
    const percent = -30 + step * i
    shades.push(adjustColor(baseColor, percent))
  }

  return shades
}

/**
 * Generates extended color palette with variations
 */
function generateExtendedPalette(baseColors, count = 12) {
  if (!baseColors || baseColors.length === 0) return []

  const extendedColors = baseColors.map(c => c.value)

  if (extendedColors.length >= count) {
    return extendedColors
  }

  // Add lighter variations
  for (const color of baseColors) {
    if (extendedColors.length >= count) break
    extendedColors.push(adjustColor(color.value, 15))
  }

  // Add darker variations
  for (const color of baseColors) {
    if (extendedColors.length >= count) break
    extendedColors.push(adjustColor(color.value, -15))
  }

  return extendedColors
}

/**
 * Chart Branding Option Component
 * Allows users to select how organization brand colors are applied to charts
 */
function ChartBrandingOption({
  Form,
  brandingType,
  setBrandingType,
  brandingColor,
  setBrandingColor,
  municipalitySettings
}) {
  // Get available brand colors
  const brandColors = useMemo(() => {
    return municipalitySettings
      ? [
          { slot: 'color_1', value: municipalitySettings.color_1, label: 'Color 1' },
          { slot: 'color_2', value: municipalitySettings.color_2, label: 'Color 2' },
          { slot: 'color_3', value: municipalitySettings.color_3, label: 'Color 3' },
          { slot: 'color_4', value: municipalitySettings.color_4, label: 'Color 4' },
          { slot: 'color_5', value: municipalitySettings.color_5, label: 'Color 5' }
        ].filter((c) => c.value) // Only show colors that are configured
      : []
  }, [municipalitySettings])

  // Add gray as an option
  const colorOptions = useMemo(() => {
    return [...brandColors, { slot: 'gray', value: '#808080', label: 'Gray' }]
  }, [brandColors])

  const hasBrandColors = brandColors.length > 0

  // Generate extended palette for all_colors mode
  const extendedPalette = useMemo(() => {
    if (!hasBrandColors) return []
    return generateExtendedPalette(brandColors, 15)
  }, [brandColors, hasBrandColors])

  // Generate shades for the selected color
  const selectedColorValue = useMemo(() => {
    if (!brandingColor) return null
    const colorObj = colorOptions.find((c) => c.slot === brandingColor)
    return colorObj?.value
  }, [brandingColor, colorOptions])

  const colorShades = useMemo(() => {
    if (!selectedColorValue) return []
    return generateShades(selectedColorValue, 7)
  }, [selectedColorValue])

  // Handle branding type change
  const handleBrandingTypeChange = (e) => {
    const newType = e.target.value
    setBrandingType(newType)

    // Set default color for single_color and shades types
    if ((newType === 'single_color' || newType === 'shades') && !brandingColor) {
      setBrandingColor(colorOptions[0]?.slot || '')
    }
    // Clear color selection for all_colors type
    if (newType === 'all_colors') {
      setBrandingColor('')
    }
    // Clear color selection when no branding is selected
    if (newType === '') {
      setBrandingColor('')
    }
  }

  // Don't show the section if no brand colors are configured
  if (!hasBrandColors) {
    return null
  }

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Chart Colors</Form.Label>
        <Form.Select size="sm" value={brandingType} onChange={handleBrandingTypeChange}>
          <option value="">Default colors</option>
          <option value="all_colors">All brand colors</option>
          <option value="single_color">Single brand color</option>
          <option value="shades">Color shades (for multi-series charts)</option>
        </Form.Select>
        <Form.Text className="text-muted">
          Choose how your organization's brand colors are applied to this chart.
        </Form.Text>
      </Form.Group>

      {/* Show color preview for all_colors */}
      {brandingType === 'all_colors' && brandColors.length > 0 && (
        <div className="mb-3">
          <small className="text-muted d-block mb-2">
            Primary brand colors:
          </small>
          <div className="d-flex gap-2 flex-wrap mb-3">
            {brandColors.map((color) => (
              <div
                key={color.slot}
                className="d-flex flex-column align-items-center"
                style={{ minWidth: '60px' }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: color.value,
                    border: '2px solid #dee2e6',
                    borderRadius: '6px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  title={`${color.label}: ${color.value}`}
                />
                <small className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                  {color.label}
                </small>
              </div>
            ))}
          </div>

          <small className="text-muted d-block mb-2">
            Extended palette (shades & tints for charts with many series):
          </small>
          <div className="d-flex gap-1 flex-wrap" style={{ maxWidth: '100%' }}>
            {extendedPalette.map((color, index) => (
              <div
                key={index}
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: color,
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
                title={color}
              />
            ))}
          </div>
          <small className="text-muted d-block mt-2" style={{ fontSize: '0.7rem' }}>
            The chart will cycle through these colors as needed.
          </small>
        </div>
      )}

      {/* Show color picker for single_color and shades types */}
      {(brandingType === 'single_color' || brandingType === 'shades') && (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Select Color</Form.Label>
            <Form.Select
              size="sm"
              value={brandingColor}
              onChange={(e) => setBrandingColor(e.target.value)}
            >
              {colorOptions.map((color) => (
                <option key={color.slot} value={color.slot}>
                  {color.label} - {color.value}
                </option>
              ))}
            </Form.Select>

            {/* Color swatches for all available colors */}
            <div className="d-flex gap-2 mt-2 flex-wrap">
              {colorOptions.map((color) => (
                <div
                  key={color.slot}
                  onClick={() => setBrandingColor(color.slot)}
                  className="d-flex flex-column align-items-center"
                  style={{
                    minWidth: '50px',
                    cursor: 'pointer',
                    opacity: brandingColor === color.slot ? 1 : 0.5,
                    transition: 'opacity 0.2s'
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: color.value,
                      border: brandingColor === color.slot ? '3px solid #0d6efd' : '2px solid #dee2e6',
                      borderRadius: '6px',
                      boxShadow: brandingColor === color.slot ? '0 2px 6px rgba(13,110,253,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                    title={`${color.label}: ${color.value}`}
                  />
                  <small className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                    {color.label}
                  </small>
                </div>
              ))}
            </div>

            <Form.Text className="text-muted d-block mt-2">
              {brandingType === 'single_color'
                ? 'This color will be used for all data in the chart.'
                : 'Multiple shades and tints of this color will be generated for different series.'}
            </Form.Text>
          </Form.Group>

          {/* Show shades scale for shades mode */}
          {brandingType === 'shades' && selectedColorValue && (
            <div className="mb-3">
              <small className="text-muted d-block mb-2">
                Color scale (darker to lighter):
              </small>
              <div className="d-flex gap-1" style={{ height: '50px' }}>
                {colorShades.map((shade, index) => (
                  <div
                    key={index}
                    className="flex-fill"
                    style={{
                      backgroundColor: shade,
                      border: '1px solid #dee2e6',
                      borderRadius: index === 0 ? '6px 0 0 6px' : index === colorShades.length - 1 ? '0 6px 6px 0' : '0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                    title={shade}
                  />
                ))}
              </div>
              <div className="d-flex justify-content-between mt-1">
                <small className="text-muted" style={{ fontSize: '0.65rem' }}>Darker</small>
                <small className="text-muted" style={{ fontSize: '0.65rem' }}>Base</small>
                <small className="text-muted" style={{ fontSize: '0.65rem' }}>Lighter</small>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default ChartBrandingOption
